import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import ImageGallery from '../components/ImageGallery.tsx';

import {
  ArrowLeftIcon,
  TruckIcon,
  CurrencyDollarIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  PhotoIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const getCategoryLabel = (category: string) => {
  const categories: Record<string, string> = {
    crlv: 'CRLV',
    manual: 'Manual',
    nota_fiscal: 'Nota Fiscal',
    fotos: 'Fotos',
    outros: 'Outros',
    contrato: 'Contrato',
    escritura: 'Escritura',
    projeto_engenharia: 'Projeto de Engenharia',
    projeto_arquitetura: 'Projeto de Arquitetura',
  };
  return categories[category] || category;
};

const VehicleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'related' | 'attachments'>('details');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  // Buscar dados do veículo da API
  const {
    data: vehicle,
    isLoading,
    error,
  } = useQuery(
    ['vehicle', id],
    async () => {
      const response = await api.get(`/vehicles/${id}`);
      
      // Processar attachments se existirem
      if (response.data.attachments && Array.isArray(response.data.attachments)) {
        response.data.attachments = response.data.attachments.map((attachment: any) => {
          // Garantir que o type exista, derivando do mimetype se necessário
          const type = attachment.type || (attachment.mimetype?.startsWith('image/') ? 'image' : 'document');
          
          return {
            ...attachment,
            type,
            preview:
              type === 'image' && attachment.url ? getBackendUrl(attachment.url) : undefined,
            file: new File([], attachment.name || 'file'),
          };
        });
      } else {
        response.data.attachments = [];
      }
      
      return response.data;
    },
    {
      enabled: !!id,
    }
  );

  // Buscar histórico do veículo
  const { data: history, isLoading: historyLoading } = useQuery(
    ['vehicle-history', id],
    async () => {
      const response = await api.get(`/vehicles/${id}/history`);
      return response.data;
    },
    {
      enabled: !!id && activeTab === 'history',
    }
  );

  const { data: relatedAssets } = useQuery<any[]>(
    ['related-vehicle', id],
    async () => {
      const response = await api.get(`/vehicles/${id}/related`);
      return response.data;
    },
    { enabled: !!id }
  );

  const { data: assetRelatedAssets } = useQuery<any[]>(
    ['related-assets', vehicle?.assetId],
    async () => {
      if (!vehicle?.assetId) return [];
      const response = await api.get(`/assets/${vehicle.assetId}/related`);
      return response.data;
    },
    { enabled: !!vehicle?.assetId }
  );

  const { data: exchanges } = useQuery<any[]>(
    ['exchanges', vehicle?.assetId],
    async () => {
      if (!vehicle?.assetId) return [];
      const response = await api.get(`/assets/${vehicle.assetId}/exchanges`);
      return response.data;
    },
    { enabled: !!vehicle?.assetId }
  );

  // Mutation para deletar veículo
  const deleteVehicleMutation = useMutation(
    async () => {
      const response = await api.delete(`/vehicles/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vehicles']);
        navigate('/vehicles');
      },
      onError: (error: any) => {
        console.error('Erro ao deletar veículo:', error);
      },
    }
  );

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center py-8">
              <p className="text-red-600">Erro ao carregar os dados do veículo.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    navigate(`/edit-vehicle/${id}`);
  };

  const handleSell = () => {
    // Navegar para a página de venda
    navigate(`/vehicles/${id}/sale`);
  };

  const handleDelete = () => {
    deleteVehicleMutation.mutate();
    setShowDeleteModal(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'in_stock':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'sold':
        return <XCircleIcon className="h-5 w-5" />;
      case 'reserved':
        return <ClockIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/vehicles')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Veículos
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {vehicle.make} {vehicle.model} ({vehicle.year})
              </h1>
              <p className="text-gray-600 mt-2">Placa: {vehicle.licensePlate}</p>
            </div>

            <div className="flex items-center space-x-3">
              <div
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(vehicle.status)}`}
              >
                {getStatusIcon(vehicle.status)}
                <span className="ml-2">{formatStatus(vehicle.status)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-gray-100">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Detalhes
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Histórico
              </button>
              <button
                onClick={() => setActiveTab('related')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'related'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Ativos Relacionados
              </button>
              <button
                onClick={() => setActiveTab('attachments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'attachments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <PhotoIcon className="h-4 w-4 mr-2 inline" />
                Anexos
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Informações Principais */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Informações Financeiras
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preço de Compra
                    </label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-gray-900">
                        {formatCurrency(vehicle.purchasePrice || 0)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preço de Venda
                    </label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-gray-900">
                        {vehicle.sellingPrice ? formatCurrency(vehicle.sellingPrice) : '-'}
                      </span>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Margem de Lucro
                    </label>
                    <div className="flex items-center">
                      {vehicle.purchasePrice &&
                      vehicle.sellingPrice &&
                      vehicle.sellingPrice > vehicle.purchasePrice ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <span
                        className={`font-semibold ${
                          vehicle.purchasePrice &&
                          vehicle.sellingPrice &&
                          vehicle.sellingPrice > vehicle.purchasePrice
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {vehicle.purchasePrice && vehicle.sellingPrice ? (
                          <>
                            {formatCurrency(vehicle.sellingPrice - vehicle.purchasePrice)}
                            <span className="text-gray-500 ml-2">
                              (
                              {(
                                ((vehicle.sellingPrice - vehicle.purchasePrice) /
                                  vehicle.purchasePrice) *
                                100
                              ).toFixed(1)}
                              %)
                            </span>
                          </>
                        ) : (
                          'Não calculado'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhes do Veículo */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <TruckIcon className="h-6 w-6 mr-2 text-primary-600" />
                  Detalhes do Veículo
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                    <p className="text-gray-900">{vehicle.make}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                    <p className="text-gray-900">{vehicle.model}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
                    <p className="text-gray-900">{vehicle.year}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Placa</label>
                    <p className="text-gray-900">{vehicle.licensePlate}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
                    <p className="text-gray-900">{vehicle.color}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Compra
                    </label>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-gray-900">
                        {vehicle.purchaseDate
                          ? new Date(vehicle.purchaseDate).toLocaleDateString('pt-BR')
                          : 'Não informado'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>

                <div className="space-y-3">
                  <button
                    onClick={handleEdit}
                    className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Editar Veículo
                  </button>

                  {vehicle.status !== 'sold' && vehicle.status !== 'exchanged' && (
                    <button
                      onClick={() => navigate(`/exchanges/new?originType=vehicle&originId=${id}`)}
                      className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
                    >
                      Registrar permuta
                    </button>
                  )}

                  {vehicle.status === 'in_stock' && (
                    <button
                      onClick={handleSell}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                      <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                      Registrar Venda
                    </button>
                  )}

                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Excluir Veículo
                  </button>
                </div>
              </div>
            </div>

              {exchanges && exchanges.length > 0 && (
                <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Permutas</h3>
                  <div className="space-y-3">
                    {exchanges.map((ex: any) => (
                      <div key={ex.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{new Date(ex.exchange_date).toLocaleDateString('pt-BR')}</p>
                          <p className="text-sm text-gray-600">Valor: {formatCurrency(Number(ex.total_value || 0))}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}
        {activeTab === 'related' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Ativos Relacionados</h3>
            {((relatedAssets && relatedAssets.length > 0) || (assetRelatedAssets && assetRelatedAssets.length > 0)) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {((relatedAssets && relatedAssets.length > 0) ? relatedAssets : (assetRelatedAssets || [])).map((relatedAsset: any) => {
                  const getRelationshipLabel = () => {
                    if (relatedAsset.relationship === 'exchange_origin') return 'Cedidos na permuta';
                    if (relatedAsset.relationship === 'exchange_result') return 'Recebidos na permuta';
                    return 'Relacionado';
                  };
                  const getTypeLabel = () => {
                    if (relatedAsset.type === 'vehicle') return 'Veículo';
                    if (relatedAsset.type === 'aircraft') return 'Aeronave';
                    if (relatedAsset.type === 'vessel') return 'Embarcação';
                    if (relatedAsset.type === 'real_estate') return 'Imóvel';
                    return 'Patrimônio';
                  };
                  return (
                    <div key={relatedAsset.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{relatedAsset.description}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(relatedAsset.status)}`}>
                          {formatStatus(relatedAsset.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Tipo: {getTypeLabel()}</p>
                      <p className="text-sm text-gray-600 mb-2">Valor: {formatCurrency(Number(relatedAsset.currentValue || 0))}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">Relacionamento: {getRelationshipLabel()}</p>
                        <button
                          onClick={() => {
                            if (relatedAsset.type === 'vehicle') navigate(`/vehicles/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'aircraft') navigate(`/aircrafts/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'vessel') navigate(`/vessels/${relatedAsset.id}`);
                            else navigate(`/assets/${relatedAsset.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-1" /> Ver
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-gray-600">Nenhum ativo relacionado encontrado.</div>
            )}
          </div>
        )}
        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Histórico do Veículo</h3>

            {historyLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Carregando histórico...</p>
              </div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((entry: any, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-gray-900">{entry.description}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(entry.date).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {entry.details && (
                          <div className="text-sm text-gray-600 mt-1">
                            {Object.entries(entry.details).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span> {String(value)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nenhum histórico registrado.</p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'attachments' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Anexos - Fotos e Documentos</h3>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {vehicle.attachments?.length || 0} arquivo(s)
                </span>
                <button
                  onClick={() => navigate(`/edit-vehicle/${id}`)}
                  className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                >
                  Adicionar/Editar
                </button>
              </div>
            </div>

            {vehicle.attachments && vehicle.attachments.length > 0 ? (
              <div className="space-y-6">
                {/* Fotos */}
                {vehicle.attachments.filter((att: any) => att.type === 'image').length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <PhotoIcon className="h-5 w-5 mr-2" />
                      Fotos ({vehicle.attachments.filter((att: any) => att.type === 'image').length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {vehicle.attachments
                        .filter((att: any) => att.type === 'image')
                        .map((attachment: any, index: number) => (
                          <div key={attachment.id} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                              <img
                                src={
                                  attachment.url
                                    ? getBackendUrl(attachment.url)
                                    : attachment.preview
                                }
                                alt={attachment.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            </div>
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                              <button
                                onClick={() => {
                                  const images = vehicle.attachments.filter((att: any) => att.type === 'image');
                                  const realIndex = images.indexOf(attachment);
                                  setGalleryStartIndex(realIndex);
                                  setShowImageGallery(true);
                                }}
                                className="opacity-0 group-hover:opacity-100 bg-white text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                            </div>
                            <p className="mt-2 text-sm text-gray-600 truncate">{attachment.name}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Documentos */}
                {vehicle.attachments.filter((att: any) => att.type === 'document').length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Documentos ({vehicle.attachments.filter((att: any) => att.type === 'document').length})
                    </h4>
                    <div className="space-y-3">
                      {vehicle.attachments
                        .filter((att: any) => att.type === 'document')
                        .map((attachment: any) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-3" />
                              <div>
                                <p className="font-medium text-gray-900">{attachment.name}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span>{attachment.size ? (attachment.size / 1024).toFixed(0) + ' KB' : '-'}</span>
                                  {attachment.category && (
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs">
                                      {getCategoryLabel(attachment.category)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                // Download do arquivo real
                                if (attachment.url) {
                                  const link = document.createElement('a');
                                  link.href = getBackendUrl(attachment.url);
                                  link.download = attachment.name;
                                  link.target = '_blank';
                                  link.click();
                                }
                              }}
                              className="flex items-center px-3 py-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                              Download
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <PhotoIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">Nenhum anexo encontrado</h4>
                <p className="text-gray-500 mb-6">
                  Este veículo ainda não possui fotos ou documentos anexados.
                </p>
                <button
                  onClick={() => navigate(`/edit-vehicle/${id}`)}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Adicionar Anexos
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Excluir Veículo"
        message="Tem certeza que deseja excluir este veículo? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
      
      {/* Modal da Galeria de Imagens */}
      {showImageGallery && vehicle.attachments && (
        <ImageGallery
          images={vehicle.attachments.filter((att: any) => att.type === 'image')}
          initialIndex={galleryStartIndex}
          onClose={() => setShowImageGallery(false)}
        />
      )}
    </div>
  );
};

export default VehicleDetail;
